import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import {
    TrendingUp,
    Zap,
    ArrowLeft,
    Bell,
    Clock,
    ShieldCheck,
    Share2,
    Activity,
    Award,
    BellRing,
    User,
    FileCode,
    Layers,
    Globe,
    Heart,
    Eye,
    Gavel,
    UserCheck,
    Cpu,
    AlertCircle
} from 'lucide-react';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useStore } from '@/store/useStore';
import { StatusModal } from '@/components/Modals/StatusModal';
import { useNotify } from '@/context/NotificationContext';
import { apiFetch, usernameMarketApi } from '@/utils/api';

// --- HELPER COMPONENTS (Copied from AuctionBid) ---

const GramIcon = ({ className }: { className?: string }) => (
    <svg viewBox="12 12 56 56" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
        <path fill="#30a1f5" d="M52.017 12.097H27.984c-3.201 0-4.802 0-6.25.448a10 10 0 0 0-3.496 1.909c-1.159.975-2.024 2.322-3.755 5.014l-7.64 11.884c-1.144 1.78-1.716 2.668-1.87 3.605a4.6 4.6 0 0 0 .263 2.45c.35.882 1.098 1.63 2.593 3.125L36.217 68.92c1.325 1.324 1.987 1.986 2.75 2.234a3.34 3.34 0 0 0 2.067 0c.763-.248 1.425-.91 2.75-2.234l28.388-28.388c1.495-1.495 2.243-2.243 2.593-3.125.31-.778.4-1.625.263-2.45-.155-.937-.727-1.826-1.87-3.605l-7.64-11.884c-1.73-2.692-2.596-4.039-3.756-5.014a10 10 0 0 0-3.496-1.91c-1.448-.447-3.048-.447-6.249-.447" />
        <path fill="#fff" d="M47.465 21.472c.39-1.055 1.883-1.055 2.274 0l2.698 7.292a1.6 1.6 0 0 0 .945.946l7.293 2.698c1.055.39 1.055 1.883 0 2.274l-7.293 2.698a1.6 1.6 0 0 0-.945.945l-2.698 7.293c-.39 1.055-1.883 1.055-2.274 0l-2.698-7.293a1.6 1.6 0 0 0-.946-.945l-7.292-2.698c-1.055-.39-1.055-1.883 0-2.274l7.292-2.698a1.6 1.6 0 0 0 .946-.946z" />
    </svg>
);

import { SpecialAuctionCard } from '@/components/Cards/SpecialAuctionCard';
import { SpecialUserCard } from '@/components/Cards/SpecialUserCard';

const UserCard = ({ user }: { user: any }) => {
    const username = (user.username || '').replace(/^@/, '');
    const displayName = user.owner?.name || username || 'USERNAME';
    const avatarUrl = user.owner?.avatar || user.img || '/icons/default-avatar.svg';
    const price = user.price || '0';
    const bg = user.color || '#1c1d22';

    return (
        <div className="flex-shrink-0">
            <SpecialAuctionCard
                displayName={displayName}
                username={username}
                avatarUrl={avatarUrl}
                price={price}
                bg={bg}
                scale={0.55}
            />
        </div>
    );
};

const SectionHeader = ({ icon: Icon, title }: { icon: any, title: string }) => (
    <div className="flex items-center gap-2 mb-4 px-1">
        <Icon size={16} className="text-blue-500" />
        <h3 className="text-[12px] font-black uppercase tracking-[0.2em] text-white/80" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {title}
        </h3>
    </div>
);

// --- MAIN COMPONENT ---

interface BrandAuctionDetailsProps {
    id: string | number;
    onBack: () => void;
}

export default function BrandAuctionDetails({ id, onBack }: BrandAuctionDetailsProps) {
    const { t } = useTranslation(['auction', 'common', 'ownership', 'profile', 'store']);
    const { impactOccurred, notificationOccurred } = useHapticFeedback();
    const { showNotification } = useNotify();
    const { user } = useStore();

    const [auction, setAuction] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [timeLeft, setTimeLeft] = useState('00:00:00');

    // Bidding State
    const [isBidModalOpen, setIsBidModalOpen] = useState(false);
    const [bidAmountInput, setBidAmountInput] = useState('');
    const [isSubmittingBid, setIsSubmittingBid] = useState(false);

    // Fetch Logic
    useEffect(() => {
        if (id && typeof id === 'object') {
            setAuction(id);
            setIsLoading(false);
            return;
        }
        if (!id) {
            setIsLoading(false);
            return;
        }
        fetchAuctionDetails();
        const interval = setInterval(fetchAuctionDetails, 3000);
        return () => clearInterval(interval);
    }, [id]);

    const fetchAuctionDetails = async () => {
        try {
            const isUmkt = String(id).startsWith('umkt_');
            const umktId = isUmkt ? String(id).replace(/^umkt_/, '') : id;

            if (isUmkt || !isNaN(Number(umktId))) {
                try {
                    const umktRes = await usernameMarketApi.auctionDetail(Number(umktId));
                    const umktData = await umktRes.json();
                    if (umktRes.ok && umktData.success && (umktData.auction || umktData.listing)) {
                        const a = umktData.auction || umktData.listing;
                        setAuction({
                            id: a.listing_id || umktId,
                            current_bid: a.current_bid || a.ask_price || a.start_price || 0,
                            start_price: a.start_price || a.ask_price || 0,
                            minBidStep: a.min_increment || 1,
                            endTime: a.ends_at,
                            card_name: a.username ? `@${a.username.replace(/^@/, '')}` : `UMKT_${umktId}`,
                            card: {
                                name: a.username ? `@${a.username.replace(/^@/, '')}` : `UMKT_${umktId}`,
                                color: '#3390EC',
                                image: a.photo_url || a.seller_avatar || '/icons/default-avatar.svg',
                                serial: String(a.listing_id || umktId)
                            },
                            seller: {
                                username: a.seller_tg_username || a.seller_name || a.username || 'Seller',
                                photo: a.photo_url || a.seller_avatar || '/icons/default-avatar.svg'
                            },
                            asset_type: a.asset_type || 'ownership',
                            bids: umktData.bids || []
                        });
                        setIsLoading(false);
                        return;
                    }
                } catch (e) { console.error('umkt fetch in BrandAuctionDetails failed', e); }
            }

            const res = await apiFetch(`/api/auctions/${id}`);
            if (res.ok) {
                const data = await res.json();
                setAuction(data);
            } else {
                const cleanId = String(id).replace(/^(umkt_|v2_|auct_)/, '');
                setAuction({
                    id: id,
                    current_bid: 10,
                    start_price: 10,
                    card_name: cleanId.includes('@') ? cleanId : `@${cleanId}`,
                    card: {
                        name: cleanId.includes('@') ? cleanId : `@${cleanId}`,
                        color: '#3390EC',
                        image: '/icons/default-avatar.svg',
                        serial: cleanId
                    },
                    seller: {
                        username: 'Market Seller',
                        photo: '/icons/default-avatar.svg'
                    },
                    asset_type: 'ownership',
                    bids: []
                });
            }
        } catch (e) {
            console.error("Fetch failed", e);
            const cleanId = String(id).replace(/^(umkt_|v2_|auct_)/, '');
            setAuction({
                id: id,
                current_bid: 10,
                start_price: 10,
                card_name: cleanId.includes('@') ? cleanId : `@${cleanId}`,
                card: {
                    name: cleanId.includes('@') ? cleanId : `@${cleanId}`,
                    color: '#3390EC',
                    image: '/icons/default-avatar.svg',
                    serial: cleanId
                },
                seller: {
                    username: 'Market Seller',
                    photo: '/icons/default-avatar.svg'
                },
                asset_type: 'ownership',
                bids: []
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Timer Logic
    useEffect(() => {
        const updateTimer = () => {
            if (!auction?.endTime) {
                setTimeLeft("Waiting");
                return;
            }
            const end = new Date(auction.endTime).getTime();
            const now = new Date().getTime();
            const diff = end - now;

            if (diff > 0) {
                const h = Math.floor(diff / (1000 * 60 * 60)).toString().padStart(2, '0');
                const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)).toString().padStart(2, '0');
                const s = Math.floor((diff % (1000 * 60)) / 1000).toString().padStart(2, '0');
                setTimeLeft(`${h}:${m}:${s}`);
            } else {
                setTimeLeft('ENDED');
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [auction?.endTime]);


    const openBidModal = () => {
        if (!auction) return;
        const current = auction.current_bid ? Number(auction.current_bid) : Number(auction.start_price);
        const min = (current * 1.05).toFixed(2);
        setBidAmountInput(min);
        setIsBidModalOpen(true);
        impactOccurred('medium');
    };

    const submitBid = async () => {
        if (isSubmittingBid || !bidAmountInput) return;
        const amount = parseFloat(bidAmountInput);

        setIsSubmittingBid(true);
        impactOccurred('heavy');
        setIsBidModalOpen(false);

        try {
            const res = await apiFetch('/api/auctions/bid', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    auctionId: auction.id,
                    userId: user?.telegram_id,
                    bidAmount: amount
                })
            });

            const data = await res.json();
            if (data.success) {
                notificationOccurred('success');
                showNotification('Bid Success', 'You are the highest bidder!', 'success');
                fetchAuctionDetails();
            } else {
                notificationOccurred('error');
                showNotification('Bid Failed', data.error || "Unknown Error", 'error');
            }
        } catch (e) {
            console.error(e);
            showNotification('Error', 'Network Error', 'error');
        } finally {
            setIsSubmittingBid(false);
        }
    };


    const handleShare = () => {
        impactOccurred('light');
        const botAppUrl = import.meta.env.VITE_TELEGRAM_BOT_APP_URL;
        const shareLink = `${botAppUrl}?startapp=auction_${id}`;
        const text = `Bid on ${assetData.username}'s Brand Auction! Current: ${assetData.price} GRAM`;
        const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(shareLink)}&text=${encodeURIComponent(text)}`;

        // @ts-ignore
        if (window.Telegram?.WebApp?.openTelegramLink) window.Telegram.WebApp.openTelegramLink(shareUrl);
        else window.open(shareUrl, '_blank');
    };

    if (isLoading) {
        return <div className="min-h-screen bg-tg-bg flex items-center justify-center text-white font-mono">Loading Auction...</div>;
    }

    if (!auction) {
        return <div className="min-h-screen bg-tg-bg flex items-center justify-center text-white font-mono">Auction Not Found</div>;
    }

    // Map Data for UI
    const assetData = {
        username: auction.card?.name || auction.card_name || 'Unknown',
        img: (auction.card?.image || auction.image_url || '').startsWith('http') || (auction.card?.image || auction.image_url || '').startsWith('/')
            ? (auction.card?.image || auction.image_url)
            : `/card_Image/${auction.card?.image || auction.image_url}`,
        color: auction.card?.color || auction.background_color || '#3390EC',
        price: parseFloat(auction.currentBid || auction.current_bid || auction.start_price || '0').toLocaleString(),
        serial: `#${auction.card?.serial || auction.card?.serial_number || auction.serial_number || '---'}`,
        rarity: `Score: ${auction.card?.rarity || auction.card?.rarity_score || auction.rarity_score || '0'}`,
        stats: {
            likes: auction.card?.likesCount || auction.likes_count || auction.card?.likes_count || '0',
            views: auction.totalViews || auction.views_count || auction.card?.views_count || '0',
            bids: auction.bidCount || auction.bid_count || '0'
        },
        extensions: auction.extensions || 0, // Ensure extensions is passed if available
        owner: {
            id: `ID: ${auction.owner_id || auction.seller_id || auction.seller?.telegramId}`,
            // Prioritize seller object from backend response
            name: auction.seller?.username || auction.owner_username || auction.seller_username || 'Unknown',
            avatar: auction.seller?.photo || auction.owner_avatar || auction.seller_photo || '/icons/default-avatar.svg',
        },
        assetType: auction.assetType || auction.asset_type || (auction.card ? auction.card.asset_type : 'ownership'),
        isNft: ((auction.assetType || auction.asset_type) === 'nft' || (auction.card && auction.card.asset_type === 'nft')),
        isPending: auction.isPending || auction.status === 'pending',
        bids: auction.bids || []
    };

    const isOwner = auction.owner_id === user?.id;

    return (
        <div className="min-h-screen bg-tg-bg text-white selection:bg-blue-500/30 overflow-x-hidden font-sans antialiased">
            <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;800&family=Space+Grotesk:wght@500;700;900&display=swap" rel="stylesheet" />

            <div className="fixed top-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-600/10 blur-[120px] rounded-full z-0" />
            <div className="fixed bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-blue-900/10 blur-[120px] rounded-full z-0" />

            {/* Navigation */}
            <nav className="h-20 pt-6 px-6 flex items-center justify-between sticky top-0 z-[100] bg-tg-bg/60 backdrop-blur-2xl border-b border-white/[0.03]">
                <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => {
                        impactOccurred('light');
                        onBack();
                    }}
                    className="w-10 h-10 rounded-xl bg-white/[0.03] border border-white/[0.05] flex items-center justify-center"
                >
                    <ArrowLeft size={18} className="text-gray-400" />
                </motion.button>

                <div className="text-center">
                    <div className="flex items-center gap-1.5 justify-center mb-0.5">
                        <div className="w-1 h-1 bg-blue-500 rounded-full animate-ping" />
                        <span className="text-[7px] font-black uppercase tracking-[0.4em] text-blue-500/80">Brand Live</span>
                    </div>
                    <h2 className="text-[11px] font-black uppercase tracking-widest text-white/90" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Auction House</h2>
                </div>

                <div className="w-10 h-10 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full border border-blue-500/50" />
                </div>
            </nav>

            <main className="max-w-md mx-auto px-6 py-8 relative z-10 space-y-8 pb-32">
                {/* HERO SECTION */}
                <div className="space-y-5 text-center flex flex-col items-center justify-center">
                    <div className="space-y-2 text-center flex flex-col items-center justify-center">
                        <motion.h1
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                            className="text-3xl font-black tracking-tighter uppercase leading-none text-white text-center"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                            {assetData.username}
                        </motion.h1>
                        <div className="flex flex-col items-center gap-1.5">
                            <div className="flex items-center justify-center gap-2">
                                <span className="px-2 py-0.5 rounded-md bg-blue-500/10 border border-blue-500/20 text-[7px] font-black text-blue-400 uppercase tracking-widest">Premium Brand</span>
                                <span className="text-[8px] text-gray-600 font-bold uppercase tracking-widest">{assetData.serial}</span>
                            </div>
                            <div className="flex items-center justify-center gap-3">
                                <div className="flex items-center gap-1">
                                    <Heart size={10} className="text-gray-600" />
                                    <span className="text-[8px] text-gray-600 font-bold uppercase tracking-widest">{assetData.stats.likes}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Eye size={10} className="text-gray-600" />
                                    <span className="text-[8px] text-gray-600 font-bold uppercase tracking-widest">{assetData.stats.views}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Gavel size={10} className="text-gray-600" />
                                    <span className="text-[8px] text-gray-600 font-bold uppercase tracking-widest">{assetData.stats.bids} Bids</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-1 text-center flex flex-col items-center justify-center">
                        <span className="text-[8px] font-bold text-gray-500 uppercase tracking-[0.3em]">Current Bid</span>
                        <div className="flex items-center justify-center gap-2 py-1">
                            <GramIcon className="w-7 h-7 flex-shrink-0" />
                            <span className="text-4xl font-black tabular-nums tracking-normal text-white drop-shadow-[0_2px_10px_rgba(255,255,255,0.15)] leading-tight">
                                {assetData.price}
                            </span>
                        </div>
                    </div>
                </div>

                {/* METRICS BENTO GRID */}
                <div className="grid grid-cols-2 gap-3">
                    <div className="group bg-white/[0.02] border border-white/[0.05] p-4 rounded-[24px] transition-all duration-500 relative overflow-hidden">
                        <span className="text-[7px] text-gray-500 font-black uppercase tracking-widest block mb-2">Time Left</span>
                        <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center`}>
                                <Clock size={16} />
                            </div>
                            <span className="text-xl font-black tracking-tighter group-hover:text-blue-400 transition-colors">{timeLeft}</span>
                        </div>
                    </div>
                    <div className="group bg-white/[0.02] border border-white/[0.05] p-4 rounded-[24px] transition-all duration-500">
                        <span className="text-[7px] text-gray-500 font-black uppercase tracking-widest block mb-2">Rarity</span>
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center text-orange-500">
                                <Award size={16} />
                            </div>
                            <span className="text-xl font-black tracking-tighter group-hover:text-orange-400 transition-colors">{assetData.rarity}</span>
                        </div>
                    </div>
                    <div className="col-span-2 group bg-white/[0.02] border border-white/[0.05] p-4 rounded-[24px] transition-all duration-500 flex items-center justify-between">
                        <div>
                            <span className="text-[7px] text-gray-500 font-black uppercase tracking-widest block mb-2">Market Demand</span>
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500">
                                    <TrendingUp size={16} />
                                </div>
                                <span className="text-xl font-black tracking-tighter group-hover:text-blue-400 transition-colors">
                                    {Math.min(99, 70 + (Number(assetData.stats.likes || 0) % 30))}%
                                </span>
                            </div>
                        </div>
                        <span className="text-[8px] font-bold text-gray-600 uppercase tracking-widest">Popularity Score</span>
                    </div>
                </div>

                {/* BID HISTORY */}
                <div className="space-y-4">
                    <SectionHeader icon={Gavel} title="Live Bids" />
                    <div className="bg-tg-secondary border border-white/[0.04] rounded-[24px] overflow-hidden">
                        {assetData.bids && assetData.bids.length > 0 ? (
                            assetData.bids.map((bid: any, i: number) => (
                                <div key={i} className="p-4 border-b border-white/[0.05] last:border-0 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <img src={bid.photo_url || '/icons/default-avatar.svg'} className="w-8 h-8 rounded-full" alt="" />
                                        <div className="flex flex-col">
                                            <span className="text-[11px] font-bold text-white">{bid.username || 'Unknown'}</span>
                                            <span className="text-[8px] text-gray-500">{new Date(bid.timestamp).toLocaleTimeString()}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-[13px] font-black tracking-tighter text-white">{Number(bid.amount).toFixed(2)}</span>
                                        <span className="text-[9px] font-bold text-blue-500">GRAM</span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="p-6 text-center text-gray-500 text-xs font-bold uppercase tracking-widest">
                                No bids yet. Be the first!
                            </div>
                        )}
                    </div>
                </div>

                {/* OWNER SECTION */}
                <div className="space-y-4">
                    <div className="group relative bg-gradient-to-r from-white/[0.03] to-transparent border border-white/[0.05] rounded-[24px] p-4 flex items-center justify-between overflow-hidden">
                        <div className="absolute top-0 right-0 w-24 h-32 bg-blue-500/5 blur-2xl rounded-full -mr-16 -mt-16" />
                        <div className="flex items-center gap-4 relative z-10">
                            <div className="relative">
                                <img src={assetData.owner.avatar} className="w-11 h-11 rounded-xl object-cover ring-1 ring-white/10" alt="Owner" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[7px] font-black text-blue-500/60 uppercase tracking-widest">Asset Owner</span>
                                <span className="text-[13px] font-bold text-white/90">{assetData.owner.name}</span>
                                <span className="text-[9px] font-mono text-gray-500">{assetData.owner.id}</span>
                            </div>
                        </div>
                        <div className="text-right relative z-10 flex flex-col items-end gap-2">
                            <div>
                                <div className="text-[8px] font-bold text-gray-600 mb-1 uppercase tracking-tighter">Asset Provider</div>
                                <div className="px-2.5 py-1 bg-blue-500/5 rounded-lg border border-white/5 text-[9px] font-black text-gray-400/80">
                                    {assetData.owner.id}
                                </div>
                            </div>

                            <div className="flex gap-1">
                                {/* Ownership Type */}
                                <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md border transition-all duration-300 ${assetData.assetType === 'ownership' ? 'bg-blue-500/20 border-blue-500/40 shadow-[0_0_10px_rgba(59,130,246,0.3)]' : 'bg-white/5 border-white/5 opacity-20'}`}>
                                    <UserCheck size={8} className={assetData.assetType === 'ownership' ? 'text-blue-400' : 'text-gray-400'} />
                                    <span className={`text-[6px] font-black uppercase tracking-tighter ${assetData.assetType === 'ownership' ? 'text-blue-400 drop-shadow-[0_0_3px_#3b82f6]' : 'text-gray-400'}`}>Ownership</span>
                                </div>
                                {/* NFT Type */}
                                <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-md border transition-all duration-300 ${assetData.assetType === 'nft' ? 'bg-[#9D4EDD]/30 border-[#9D4EDD]/60 shadow-[0_0_15px_#9D4EDD]' : 'bg-white/5 border-white/5 opacity-20'}`}>
                                    <Cpu size={10} className={assetData.assetType === 'nft' ? 'text-[#9D4EDD]' : 'text-gray-400'} />
                                    <span className={`text-[7px] font-black uppercase tracking-tighter ${assetData.assetType === 'nft' ? 'text-[#9D4EDD] drop-shadow-[0_0_3px_#9D4EDD]' : 'text-gray-400'}`}>NFT</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* USER TYPE / ACCESS LEVEL SECTION */}
                    <div className="bg-tg-secondary border border-white/[0.04] rounded-[28px] overflow-hidden shadow-xl shadow-black/20 relative">
                        <div className="p-4 bg-white/[0.02] border-b border-white/[0.05] flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <ShieldCheck size={14} className="text-blue-500" />
                                <span className="text-[10px] font-black uppercase text-gray-500">Ownership Level</span>
                            </div>
                            <span className="text-[10px] font-black text-yellow-400 uppercase px-2.5 py-1 bg-yellow-400/10 rounded-lg flex items-center gap-1.5 shadow-[0_0_10px_rgba(250,204,21,0.2)] border border-yellow-400/20">
                                <Award size={10} />
                                Level {auction.card?.access_level || 5}: {['Public', 'Premium', 'VIP', 'Ultra', 'Legend', 'Founder'][(auction.card?.access_level || 5) - 1] || 'Legend'}
                            </span>
                        </div>
                        <div className="p-3 grid grid-cols-3 gap-2">
                            {[
                                { name: 'Public', icon: User, color: 'text-gray-400' },
                                { name: 'Premium', icon: Heart, color: 'text-blue-300' },
                                { name: 'VIP', icon: FileCode, color: 'text-purple-400' },
                                { name: 'Ultra', icon: Zap, color: 'text-orange-400' },
                                { name: 'Legend', icon: Award, color: 'text-yellow-400' },
                                { name: 'Founder', icon: Globe, color: 'text-cyan-400' }
                            ].map((lvl, i) => {
                                const isActive = i === ((auction.card?.access_level || 5) - 1);
                                const Icon = lvl.icon;
                                return (
                                    <div
                                        key={lvl.name}
                                        className={`relative py-3 rounded-xl text-center border transition-all duration-300 flex flex-col items-center gap-1.5 ${isActive
                                            ? 'bg-yellow-400/20 border-yellow-400/40 shadow-[0_4px_15px_rgba(250,204,21,0.2)] scale-105 z-10'
                                            : 'bg-white/[0.02] border-white/[0.05] opacity-30 hover:opacity-50'
                                            }`}
                                    >
                                        <Icon size={14} className={`${isActive ? 'text-yellow-400' : lvl.color} drop-shadow-[0_0_5px_currentColor]`} />
                                        <div className="flex flex-col">
                                            <span className={`text-[9px] font-black uppercase block ${isActive ? 'text-yellow-400' : 'text-white/90'}`}>Lvl {i + 1}</span>
                                            <span className={`text-[7px] font-bold uppercase block ${isActive ? 'text-yellow-400/70' : 'text-white/40'}`}>{lvl.name}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* NEW SECTION: PROVENANCE & SPECS */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2.5 px-1">
                        <ShieldCheck size={14} className="text-blue-500" />
                        <h3 className="text-lg font-black uppercase tracking-tight italic" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Provenance & Specs</h3>
                    </div>

                    <div className="bg-[#070b14]/50 border border-white/[0.05] rounded-[28px] p-5 space-y-5 relative overflow-hidden group">
                        {/* Subtle tech grid background */}
                        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />

                        <div className="grid grid-cols-1 gap-5 relative z-10">
                            {/* Smart Contract Info */}
                            <div className="flex items-center justify-between group/item">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-xl bg-white/[0.03] border border-white/[0.05] flex items-center justify-center text-gray-400 group-hover/item:text-blue-400 transition-colors">
                                        <FileCode size={18} />
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">Smart Contract</span>
                                        <span className="text-[11px] font-black text-white/90 tracking-wider">GRAM-ERC721-V3</span>
                                    </div>
                                </div>
                                <div className="px-2 py-1 bg-green-500/10 rounded-md border border-green-500/20 text-[7px] font-black text-green-400 uppercase tracking-widest">Verified</div>
                            </div>

                            {/* Divider */}
                            <div className="h-px bg-gradient-to-r from-transparent via-white/[0.05] to-transparent" />

                            {/* Bid Step & Fee Row */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Layers size={12} className="text-blue-500/70" />
                                        <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">Min. Bid Step</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 px-1">
                                        <GramIcon className="w-3 h-3" />
                                        <span className="text-sm font-black text-white tabular-nums tracking-tight">{parseFloat(String(auction.minBidStep || 0.1)).toFixed(2)} GRAM</span>
                                    </div>
                                </div>

                                <div className="space-y-2 border-l border-white/[0.05] pl-4">
                                    <div className="flex items-center gap-2">
                                        <Globe size={12} className="text-blue-500/70" />
                                        <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">Network Fee</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 px-1">
                                        <GramIcon className="w-3 h-3 opacity-60" />
                                        <span className="text-sm font-black text-white/60 tabular-nums tracking-tight">0.02 GRAM</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Decorative accent bar */}
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-12 bg-blue-500 rounded-r-full shadow-[0_0_15px_rgba(59,130,246,0.5)]" />
                    </div>
                </div>

            </main>

            {/* FOOTER ACTION */}
            <div className="fixed bottom-6 inset-x-0 px-8 z-[200]">
                <div className="max-w-md mx-auto p-1.5 bg-white/[0.03] backdrop-blur-3xl border border-white/[0.08] rounded-[24px] shadow-[0_25px_50px_rgba(0,0,0,0.8)] flex items-center gap-2">
                    <motion.button
                        whileTap={{ scale: 0.9 }}
                        className="h-11 w-11 rounded-[18px] flex items-center justify-center border bg-white/5 border-white/5 text-gray-500 hover:text-white"
                    >
                        <Bell size={18} />
                    </motion.button>

                    {isOwner ? (
                        <div className="flex-1 bg-gray-500/20 text-gray-400 h-11 rounded-[18px] flex items-center justify-center font-bold text-xs">
                            You Own This
                        </div>
                    ) : (
                        <motion.button
                            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                            onClick={openBidModal}
                            disabled={timeLeft === 'ENDED' || isSubmittingBid}
                            className="flex-1 bg-gradient-to-r from-blue-600 to-blue-500 text-white h-11 rounded-[18px] font-black uppercase tracking-widest text-[10px] shadow-[0_10px_20px_rgba(37,99,235,0.3)] flex items-center justify-center gap-2.5 disabled:opacity-50"
                        >
                            <Zap size={14} fill="white" className={(isSubmittingBid || timeLeft === 'ENDED') ? '' : 'animate-pulse'} />
                            {isSubmittingBid ? 'Submitting...' : timeLeft === 'ENDED' ? 'Auction Ended' : 'Place Bid Now'}
                        </motion.button>
                    )}
                </div>
            </div>

            {/* BID MODAL */}
            <AnimatePresence>
                {isBidModalOpen && (
                    <div className="fixed inset-0 z-[250] flex items-end justify-center px-4 pb-6">
                        <motion.div
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="absolute inset-0 bg-black/80 backdrop-blur-md"
                            onClick={() => setIsBidModalOpen(false)}
                        />
                        <motion.div
                            initial={{ y: 200, opacity: 0, scale: 0.95 }}
                            animate={{ y: 0, opacity: 1, scale: 1 }}
                            exit={{ y: 200, opacity: 0, scale: 0.95 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className="bg-[#1c1c1e] w-full max-w-sm rounded-[32px] overflow-hidden shadow-2xl relative z-10 border border-white/5"
                        >
                            <div className="p-6">
                                <h3 className="text-xl font-black text-white text-center mb-6 uppercase">Place Bid</h3>
                                <div className="relative mb-6">
                                    <input
                                        type="number"
                                        value={bidAmountInput}
                                        onChange={(e) => setBidAmountInput(e.target.value)}
                                        className="w-full bg-black/50 border border-white/10 rounded-2xl py-4 px-4 text-2xl font-black text-white text-center focus:border-blue-500 outline-none transition-colors"
                                        autoFocus
                                    />
                                    <div className="text-center mt-2 text-xs text-gray-500 font-bold uppercase tracking-wider">
                                        Min Bid: {(Number(auction.current_bid || auction.start_price) * 1.05).toFixed(2)} GRAM
                                    </div>
                                </div>
                                <button
                                    onClick={submitBid}
                                    className="w-full py-4 bg-blue-500 rounded-xl font-black text-white uppercase tracking-widest"
                                >
                                    Confirm Bid
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

        </div>
    );
}
